import React, {
  memo,
  useMemo,
  useRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { VariableSizeList } from "react-window";
import { useSelector } from "react-redux";
import VirtualizedAccordionRow from "./VirtualizedAccordionRow";
import { useWindowResize } from "../../hooks/useWindowResize";
import { notesApi } from "../../services/notesApi";
import { NOTE_TYPE_VALUE } from "../../utils";

const VirtualizedAccordionContext = React.createContext(null);

const VirtualizedAccordionRowRenderer = memo((props) => {
  const { style, index } = props;
  const {
    scope,
    itemData,
    setSize,
    windowWidth,
    updateSelections,
    getProcessedRow,
    serverSelections,
    retailerNotes,
    plannerNotes,
  } = useContext(VirtualizedAccordionContext);

  const handleProcessedRow = useCallback(
    (index) => (updatedRow) => {
      getProcessedRow(updatedRow, index);
    },
    [getProcessedRow]
  );

  return (
    <div style={style}>
      <VirtualizedAccordionRow
        row={itemData}
        index={index}
        setSize={setSize}
        windowWidth={windowWidth}
        scope={scope}
        parentId={itemData[index].id}
        subRows={itemData[index]?.subRows}
        getProcessedRow={handleProcessedRow(index)}
        serverSelections={serverSelections[itemData[index].id]}
        updateSelections={updateSelections}
        retailerNotes={retailerNotes}
        plannerNotes={plannerNotes}
      />
    </div>
  );
});

const SubAccordion = ({
  data,
  serverSelections = [],
  updateSelections,
  scope,
  getProcessedRow,
}) => {
  const store = useSelector((store) => store.global.store);
  const itemData = useMemo(() => data[scope], [data, scope]);
  const listRef = useRef();
  const [currentOffset, setCurrentOffset] = useState(0);
  const sizeMap = useRef({});
  const setSize = useCallback((index, size) => {
    sizeMap.current = { ...sizeMap.current, [index]: size };
    if (listRef.current) {
      listRef.current.resetAfterIndex(index);
    }
  }, []);
  const [windowWidth] = useWindowResize();

  const { data: plannerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.plannerNotes,
      storeId: store.id,
    });

  const { data: retailerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.retailer,
      storeId: store.id,
    });

  const plannerNotes = useMemo(() => {
    return plannerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
  }, [plannerNotesData]);

  const retailerNotes = useMemo(() => {
    const list = retailerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
    return list;
  }, [retailerNotesData]);

  const getSize = (index) => {
    return sizeMap.current[index] || 106;
  };

  const getItemKey = useCallback(
    (index) => {
      return itemData[index].id ?? index;
    },
    [itemData]
  );

  const virtualListItemData = useMemo(
    () => ({
      itemData,
      scope,
      setSize,
      windowWidth,
      updateSelections,
      getProcessedRow,
      serverSelections,
      retailerNotes,
      plannerNotes,
    }),
    [
      scope,
      itemData,
      setSize,
      windowWidth,
      updateSelections,
      getProcessedRow,
      serverSelections,
      retailerNotes,
      plannerNotes,
    ]
  );

  useEffect(() => {
    setTimeout(() => {
      setCurrentOffset(
        Math.ceil(
          Math.ceil(
            window.innerWidth -
              Math.ceil(
                window.document.documentElement.getBoundingClientRect().width
              )
          ) - 2
        )
      );
    }, 50);
  }, []);

  const currentWidth = Math.ceil(
    document.getElementById("acc-container")?.offsetWidth - currentOffset
  );

  if (!itemData.length) {
    return <></>;
  }

  return (
    <VirtualizedAccordionContext.Provider value={virtualListItemData}>
      <VariableSizeList
        ref={listRef}
        height={515}
        width={currentWidth}
        itemCount={itemData.length}
        itemSize={getSize}
        itemKey={getItemKey}
        overscanCount={3}
        estimatedItemSize={106}
      >
        {VirtualizedAccordionRowRenderer}
      </VariableSizeList>
    </VirtualizedAccordionContext.Provider>
  );
};

export default memo(SubAccordion);
